import { AUTO_REPLY_MESSAGE_MAX } from '@chat-emp/shared';

import { Button } from '../components/Button.js';
import { Switch } from '../components/Switch.js';
import type { Form } from '../fixtures/forms.js';
import { plural } from '../util/format.js';
import { isSendableReply, type AutoReply } from './AutoReply.js';
import styles from './AutoReplyPanel.module.css';

interface AutoReplyPanelProps {
  /**
   * The reply as it is being edited, held by the store rather than by this
   * column — the preview beside it is looking at the same value, and typing has
   * to redraw the bubble immediately. See `useAutoReply`.
   */
  draft: AutoReply;
  /** What can be attached: the employer's own library plus the shared
   *  templates, resolved by the page (the mock's shape — see useForms). */
  forms: Form[];
  /** The library has not answered yet, or its read failed. An attached form
   *  missing from an empty `forms` is then unknown, not deleted. */
  formsPending: boolean;
  /** Whether there is anything to save. */
  dirty: boolean;
  saving: boolean;
  onChange: (reply: AutoReply) => void;
  onSave: () => void;
  onWriteForm: () => void;
}

/**
 * The auto reply, as the column beside its preview.
 *
 * One reply for the whole account, so there is no list here and nothing to
 * select: the settings *are* the column, and the pane shows what they produce.
 * That split is why this section earns two columns at all — a settings form
 * alone would be a narrow page with a lot of white beside it, and a preview
 * alone would be a picture of something you could not change.
 *
 * Message first, form second, and the order is the argument. The message is the
 * reply; a form is something the reply may also carry. Built the other way round
 * it becomes "pick a questionnaire, then write a covering note", which is the
 * impression the whole feature is trying not to make.
 */
export function AutoReplyPanel({
  draft,
  forms,
  formsPending,
  dirty,
  saving,
  onChange,
  onSave,
  onWriteForm,
}: AutoReplyPanelProps) {
  const used = [...draft.message].length;
  // A saved reply can name a form since deleted from the library (the store
  // keeps the id opaquely; nothing cascades yet). Named honestly rather than
  // letting the select silently show "No form" over a value it still holds —
  // and only called deleted once the library has actually said so, since an
  // unread library is empty too.
  const unnamedAttachment = draft.formId !== null && !forms.some((f) => f.id === draft.formId);

  return (
    <div className={styles.panel}>
      <div className={styles.header}>
        <h1 className={styles.title}>Auto reply</h1>
      </div>

      <div className={styles.body}>
        <p className={styles.lede}>
          Sent the moment a candidate messages for the first time, before anyone has answered. One
          reply for the whole company: it goes out under the name of whoever holds the conversation
          and counts as their reply.
        </p>

        {/* A switch, because this is the feature being on or off rather than an
            option on it (see `Switch`). The word beside it changes with the
            state, so the row can be read without decoding which end the knob is
            at. */}
        <label className={styles.toggle}>
          <Switch
            checked={draft.enabled}
            onChange={() => onChange({ ...draft, enabled: !draft.enabled })}
          />
          <span className={styles.toggleLabel}>{draft.enabled ? 'On' : 'Off'}</span>
        </label>

        <section className={styles.section}>
          <h2 className={styles.sectionTitle}>Message</h2>
          <textarea
            className={styles.text}
            rows={5}
            value={draft.message}
            // The store's own cap, so the browser stops the typing rather than
            // the save refusing it. The counter below only appears near the
            // limit — a running tally on every keystroke is a nag about a
            // ceiling almost nobody meets.
            maxLength={AUTO_REPLY_MESSAGE_MAX}
            placeholder="Thanks for getting in touch. Someone from our team will read this shortly."
            aria-label="Auto reply message"
            onChange={(event) => onChange({ ...draft, message: event.target.value })}
          />
          {used > AUTO_REPLY_MESSAGE_MAX - 50 && (
            <p className={styles.count}>
              {used} / {AUTO_REPLY_MESSAGE_MAX}
            </p>
          )}
          {draft.enabled && !isSendableReply(draft) && (
            <p className={styles.warn}>Nothing sends until this has something in it.</p>
          )}
        </section>

        <section className={styles.section}>
          <h2 className={styles.sectionTitle}>Form</h2>
          <p className={styles.sectionNote}>Optional. It arrives as a card under the message.</p>
          <select
            className={styles.select}
            value={draft.formId ?? ''}
            aria-label="Form to attach"
            onChange={(event) =>
              // Empty is a value here, not a missing one: "No form, message
              // only" clears the stored form rather than leaving it alone.
              onChange({ ...draft, formId: event.target.value === '' ? null : event.target.value })
            }
          >
            <option value="">No form, message only</option>
            {unnamedAttachment && (
              <option value={draft.formId ?? ''}>
                {formsPending ? 'Attached form' : 'No longer in your library'}
              </option>
            )}
            {forms.map((form) => (
              <option key={form.id} value={form.id}>
                {form.name} · {plural(form.fields.length, 'question')}
              </option>
            ))}
          </select>
          {/* Every form in the library is sendable, so the list above is the
              whole of it and there is nothing to explain away. This is the way
              out to write another. */}
          <button type="button" className={styles.manage} onClick={onWriteForm}>
            Create a new form
          </button>
        </section>
      </div>

      {/* Explicit, and disabled until there is something to write. Editing here
          redraws the preview instantly, which is what makes an unsaved state
          worth having: the employer can try wording against the real bubble and
          walk away from it. */}
      <div className={styles.footer}>
        {dirty && <span className={styles.unsaved}>Unsaved changes</span>}
        <Button onClick={onSave} disabled={saving || !dirty}>
          Save
        </Button>
      </div>
    </div>
  );
}
